/**
 * IdentityPreview — IDENTITY:P3.A Live Terminal Header Preview
 *
 * Shows a TerminalHeader preview with current identity details.
 * Accepts identity data as props for standalone testability.
 * Action buttons for changing alias and avatar.
 *
 * Solution Proposal: SOL-20260309-IDENTITY-P3
 */

import React from 'react';
import TerminalHeader from './TerminalHeader';

export default function IdentityPreview({
  identity,
  onChangeAlias,
  onChangeAvatar,
}) {
  if (!identity) {
    return null;
  }

  const {
    alias,
    avatarUrl,
    fallbackLetter,
    fallbackColor,
    rankTier,
    totalPoints,
  } = identity;

  return (
    <section aria-label="Identity preview" className="space-y-4">
      <TerminalHeader
        alias={alias}
        avatarUrl={avatarUrl}
        fallbackLetter={fallbackLetter}
        fallbackColor={fallbackColor}
      />

      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs text-zinc-400 font-maestro-mono uppercase tracking-wider">
              Alias
            </span>
            <p className="text-[#D4A03C] font-maestro-mono font-bold text-sm">
              {alias || 'Not set'}
            </p>
          </div>
          {onChangeAlias && (
            <button
              type="button"
              onClick={onChangeAlias}
              className="text-xs text-[#D4A03C] hover:text-orange-400 font-maestro-mono border border-zinc-700 hover:border-[#D4A03C] rounded px-3 py-1 transition-colors"
              aria-label="Change alias"
            >
              Change
            </button>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs text-zinc-400 font-maestro-mono uppercase tracking-wider">
              Avatar
            </span>
            <p className="text-zinc-300 font-maestro-mono text-sm">
              {avatarUrl ? 'Custom avatar' : 'Letter badge'}
            </p>
          </div>
          {onChangeAvatar && (
            <button
              type="button"
              onClick={onChangeAvatar}
              className="text-xs text-[#D4A03C] hover:text-orange-400 font-maestro-mono border border-zinc-700 hover:border-[#D4A03C] rounded px-3 py-1 transition-colors"
              aria-label="Change avatar"
            >
              Change
            </button>
          )}
        </div>

        {rankTier && (
          <div>
            <span className="text-xs text-zinc-400 font-maestro-mono uppercase tracking-wider">
              Rank
            </span>
            <p className="text-[#D4A03C] font-maestro-mono font-bold text-sm">
              {rankTier}
            </p>
          </div>
        )}

        <div>
          <span className="text-xs text-zinc-400 font-maestro-mono uppercase tracking-wider">
            Points
          </span>
          <p className="text-zinc-300 font-maestro-mono text-sm">
            {totalPoints != null ? totalPoints.toLocaleString() : '0'}
          </p>
        </div>
      </div>
    </section>
  );
}
